import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Button, Skeleton, TextField, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useFormik } from 'formik';
import React, { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import updateCollectionSequence from 'src/api/collection/updateCollectionSequence';
import ActionsButton from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/EditCollectionDetails/ActionsButton';
import usePlaylistItemStyles from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/playlistItemStyles';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';
import EditCollectionItem from './EditCollectionItem';

interface ContentObject {
  id: string;
  name: string;
  mediatype?: string;
  inpoint?: string;
  timecode?: string;
  formatSet: {
    edges: Array<{
      node: {
        id: string;
        fmtType?: {
          slug: string;
        };
      };
    }>;
  };
}

interface CollectionItemNode {
  contentObject: ContentObject;
}

interface CollectionItemEdge {
  node: CollectionItemNode;
}

interface CollectionData {
  collection?: {
    name: string;
    items: {
      edges: CollectionItemEdge[];
    };
  };
}

interface FormValues {
  name: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  myCollectionContainer: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  editPaper: {
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
    borderTop: '2px solid',
    borderImage: `linear-gradient(90deg, ${theme.palette.secondary.main}, transparent) 1`,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },
  playlistTitleAndAction: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: `${theme.spacing(0.75)} 12px`,
    flexShrink: 0,
  },
  playlistName: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: 13,
    maxWidth: 160,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemCount: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.selected,
    padding: '1px 6px',
    borderRadius: 8,
  },
  newNameField: {
    marginTop: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
    },
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    margin: `${theme.spacing(0.5)} 0`,
    flexShrink: 0,
  },
  form: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    flexShrink: 0,
  },
}));

const EditCollectionDetails: React.FC = () => {
  const classes = useStyles();
  const sharedClasses = usePlaylistItemStyles();
  const [collectionItems, setCollectionItems] = useState<CollectionItemEdge[]>([]);
  const [flashItemId, setFlashItemId] = useState<string | null>(null);
  const [showPendingSkeleton, setShowPendingSkeleton] = useState(false);
  const prevCountRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const viewState = useReactiveVar(viewStateVar);
  const [sendRenamingRequest] = useMutation(editCollection);
  const [sendUpdateCollectionSequence] = useMutation(updateCollectionSequence);

  const { loading: loading1, data: data1 } = useQuery<CollectionData>(assetsByCollectionIdPlaylist, {
    variables: { id: viewState.selectedCollectionId },
    fetchPolicy: 'cache-and-network',
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'CARD_AKOMI',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop: () => {
      setShowPendingSkeleton(true);
    },
  });

  const cancelRenamingPlaylist = (): void => {
    viewStateVar({
      ...viewStateVar(),
      renamePlaylist: null,
    });
  };

  const moveCard = (assetId: string, toIndex: number): void => {
    sendUpdateCollectionSequence({
      variables: {
        collectionId: viewState.selectedCollectionId,
        assetId,
        sequence: toIndex,
      },
      refetchQueries: [assetsByCollectionIdPlaylist],
    });
  };

  const hoverCard = useCallback(
    (fromIndex: number, toIndex: number): void => {
      const element = collectionItems[fromIndex];
      setCollectionItems((items) => {
        const newItems = [...items];
        newItems.splice(fromIndex, 1);
        newItems.splice(toIndex, 0, element);
        return newItems;
      });
    },
    [collectionItems],
  );

  useEffect(() => {
    if (data1?.collection?.items?.edges) {
      const newEdges = [...data1.collection.items.edges];
      if (newEdges.length > prevCountRef.current && prevCountRef.current > 0) {
        setShowPendingSkeleton(false);
        const newItem = newEdges[newEdges.length - 1];
        if (newItem) {
          setFlashItemId(newItem.node.contentObject.id);
          setTimeout(() => {
            if (listRef.current) {
              listRef.current.scrollTop = listRef.current.scrollHeight;
            }
          }, 50);
          setTimeout(() => setFlashItemId(null), 1500);
        }
      }
      prevCountRef.current = newEdges.length;
      setCollectionItems(newEdges);
    }
    viewStateVar({
      ...viewStateVar(),
      collectionData: data1 as unknown as unknown[],
    });
  }, [data1]);

  const formik1 = useFormik<FormValues>({
    initialValues: {
      name: data1?.collection?.name || '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values) => {
      const { name } = values;
      sendRenamingRequest({
        variables: {
          collectionId: viewState.selectedCollectionId,
          name,
          assetIds: data1?.collection?.items.edges.map((edge) => edge.node.contentObject.id) || [],
          collectionType: 'PLAYLIST',
          autoSeq: true,
        },
        refetchQueries: [assetsByCollectionIdPlaylist],
      }).then(() => {
        viewStateVar({
          ...viewStateVar(),
          renamePlaylist: null,
        });
      });
    },
  });

  const handleKeyDown = (k: KeyboardEvent<HTMLDivElement>): void => {
    if (k.key === 'Enter') {
      formik1.handleSubmit();
    }
  };

  if (!data1 || loading1) {
    return (
      <div className={classes.myCollectionContainer}>
        <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{ mb: 0.5 }} />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={sharedClasses.skeletonRow}>
            <Skeleton variant="rectangular" width={32} height={22} animation="wave" sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="text" width={`${60 + (i % 3) * 10}%`} height={16} animation="wave" />
          </div>
        ))}
      </div>
    );
  }

  if (viewState.renamePlaylist) {
    return (
      <div
        ref={(node) => {
          drop(node);
        }}
        className={`${classes.myCollectionContainer} ${isOver ? sharedClasses.dropZoneActive : ''}`}
      >
        <div className={classes.playlistTitleAndAction}>
          <Typography className={classes.playlistName}>{data1?.collection?.name}</Typography>
          <span className={classes.itemCount}>{data1?.collection?.items.edges.length ?? 0}</span>
        </div>
        <form onSubmit={formik1.handleSubmit}>
          <TextField
            className={classes.newNameField}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            defaultValue={data1?.collection?.name}
            {...formik1.getFieldProps('name')}
            onKeyDown={handleKeyDown}
          />
          <div className={classes.buttonContainer}>
            <Button variant="contained" color="primary" type="submit" size="small">
              Save
            </Button>
            <Button color="secondary" onClick={cancelRenamingPlaylist} size="small">
              Cancel
            </Button>
          </div>
        </form>
        <Paper ref={listRef} className={`${classes.editPaper} ${sharedClasses.thinScrollbar}`}>
          {collectionItems.map((ss, index) => (
            <EditCollectionItem
              ss={ss}
              hoverCard={hoverCard}
              collectionData={collectionItems}
              index={index}
              moveCard={moveCard}
              id={ss.node.contentObject.id}
              key={ss.node.contentObject.id}
              akomiType={ss.node.contentObject.mediatype}
              flashId={flashItemId}
            />
          ))}
          {showPendingSkeleton && (
            <div className={sharedClasses.pendingSkeleton}>
              <Skeleton
                variant="rectangular"
                width={36}
                height={24}
                animation="wave"
                sx={{ borderRadius: 0.5, flexShrink: 0 }}
              />
              <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ flex: 1 }} />
            </div>
          )}
        </Paper>
      </div>
    );
  } else {
    return (
      <div
        ref={(node) => {
          drop(node);
        }}
        className={`${classes.myCollectionContainer} ${isOver ? sharedClasses.dropZoneActive : ''}`}
      >
        <div className={classes.playlistTitleAndAction}>
          <Typography className={classes.playlistName}>{data1?.collection?.name}</Typography>
          <span className={classes.itemCount}>{data1?.collection?.items.edges.length ?? 0}</span>
          <ActionsButton collectionItems={collectionItems} />
        </div>
        <Paper ref={listRef} className={`${classes.editPaper} ${sharedClasses.thinScrollbar}`}>
          {collectionItems.map((ss, index) => (
            <EditCollectionItem
              clickForPreview
              hoverCard={hoverCard}
              ss={ss}
              collectionData={collectionItems}
              index={index}
              moveCard={moveCard}
              id={ss.node.contentObject.id}
              key={ss.node.contentObject.id}
              akomiType={ss.node.contentObject.mediatype}
              flashId={flashItemId}
            />
          ))}
          {showPendingSkeleton && (
            <div className={sharedClasses.pendingSkeleton}>
              <Skeleton
                variant="rectangular"
                width={36}
                height={24}
                animation="wave"
                sx={{ borderRadius: 0.5, flexShrink: 0 }}
              />
              <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ flex: 1 }} />
            </div>
          )}
        </Paper>
      </div>
    );
  }
};

export default EditCollectionDetails;
